/**
 * Tracking Automation Section — P0 Sprint (26 Feb 2026).
 *
 * Compact Operations-hub met drie tabs:
 *   1. Dashboard       — KPIs + segmenten (vandaag/komend/mislukt/geskipt/uitgevoerd)
 *   2. Consolidatie-planner — één overzicht met ETD/ETA + next scheduled event
 *   3. Statusflow-templates — read-only browser (edit komt in vervolg-sprint)
 *
 * Data-testids op elk interactief element voor testing_agent + regressie.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../../ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../../ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Badge } from "../../ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../ui/table";
import { toast } from "sonner";
import {
  RefreshCw, Play, AlertTriangle, CheckCircle2, Clock,
  Boxes, Ship, Plane, Truck, Package, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Sprint P0 bugfix 27-Feb-2026 — de canonical token-key is 'cargo_admin_token'
// (zie AuthContext.jsx). De legacy key 'admin_token' die deze component eerder
// gebruikte was leeg → dashboard kreeg 401/403 → "Dashboard kan niet geladen
// worden". Als extra veiligheid fallback naar legacy key voor oude sessies.
const authHeaders = () => {
  const token =
    localStorage.getItem("cargo_admin_token") ||
    localStorage.getItem("admin_token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATE_COLORS = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  processing: "bg-purple-50 text-purple-700 border-purple-200",
  executed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  skipped: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
};

const TRANSPORT_ICONS = {
  sea: Ship,
  air: Plane,
  ground: Truck,
  courier: Package,
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("nl-NL", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function EventRow({ ev }) {
  return (
    <TableRow data-testid={`ops-event-row-${ev.id}`}>
      <TableCell>
        <span className="font-mono text-xs text-gray-500">
          {ev.entity_type === "shipment" ? "W" : "C"}
        </span>{" "}
        <span data-testid={`ops-event-label-${ev.id}`}>{ev.entity_label || ev.entity_id.slice(0, 8)}</span>
      </TableCell>
      <TableCell className="text-sm">{ev.new_status}</TableCell>
      <TableCell className="text-xs text-gray-600">{fmtDate(ev.scheduled_for)}</TableCell>
      <TableCell>
        <Badge variant="outline" className={STATE_COLORS[ev.state] || ""}>
          {ev.state}
        </Badge>
      </TableCell>
      <TableCell className="text-xs text-gray-500">
        {ev.state_reason || ev.template_code || "—"}
      </TableCell>
    </TableRow>
  );
}

function KpiCard({ label, value, icon: Icon, tone = "default", testId }) {
  const tones = {
    default: "border-gray-200",
    ok: "border-emerald-200 bg-emerald-50/40",
    warn: "border-amber-200 bg-amber-50/40",
    bad: "border-red-200 bg-red-50/40",
    info: "border-blue-200 bg-blue-50/40",
  };
  return (
    <div className={`border ${tones[tone]} rounded-md p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wide">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </div>
      <div className="mt-2 text-2xl font-black text-[#082c50] font-chivo">
        {value ?? 0}
      </div>
    </div>
  );
}

function OpsDashboard() {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState("today");
  const [retryLoading, setRetryLoading] = useState({});

  const fetchDash = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API}/admin/tracking-automation/dashboard`,
        { headers: authHeaders() },
      );
      setDash(r.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || "Onbekende fout";
      setError({ status, detail });
      toast.error(`Dashboard laden mislukt (HTTP ${status || "-"}): ${detail}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDash();
    const iv = setInterval(fetchDash, 60_000); // auto-refresh 1 min
    return () => clearInterval(iv);
  }, [fetchDash]);

  const kpis = dash?.kpis || {};
  const segments = dash?.segments || {};

  const handleRetry = async (eventId) => {
    setRetryLoading((s) => ({ ...s, [eventId]: true }));
    try {
      await axios.post(
        `${API}/admin/tracking-automation/events/${eventId}/retry`,
        {}, { headers: authHeaders() },
      );
      toast.success("Event opnieuw ingepland");
      fetchDash();
    } catch (e) {
      toast.error(`Retry mislukt: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRetryLoading((s) => ({ ...s, [eventId]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="ops-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-[#082c50] font-chivo">
            Tracking Operations
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Real-time overzicht van geplande, uitgevoerde en mislukte statusupdates.
          </p>
        </div>
        <Button
          variant="outline" onClick={fetchDash} disabled={loading}
          data-testid="ops-dashboard-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Verversen
        </Button>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 px-4 py-3 rounded-md flex items-start gap-3"
             data-testid="ops-dashboard-error-banner">
          <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-bold text-red-900">
              Dashboard kan niet geladen worden
            </div>
            <div className="text-xs text-red-800 mt-0.5">
              {error.status === 401 || error.status === 403
                ? "Uw sessie is verlopen of u heeft geen 'tracking:manage' permissie. Log opnieuw in of vraag een beheerder."
                : "Er ging iets mis bij het ophalen van de status-updates."}
            </div>
            <div className="text-[11px] text-red-700 mt-1 font-mono">
              HTTP {error.status || "-"}: {error.detail}
            </div>
          </div>
          <Button
            size="sm" variant="outline" onClick={fetchDash} disabled={loading}
            className="text-xs border-red-300 text-red-800 hover:bg-red-100"
            data-testid="ops-dashboard-retry-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Opnieuw proberen
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Vandaag gepland" value={kpis.scheduled_today}
                  icon={Clock} tone="info"
                  testId="ops-kpi-scheduled-today" />
        <KpiCard label="Vandaag uitgevoerd" value={kpis.executed_today}
                  icon={CheckCircle2} tone="ok"
                  testId="ops-kpi-executed-today" />
        <KpiCard label="Mislukt (totaal)" value={kpis.failed_total}
                  icon={AlertTriangle} tone={kpis.failed_total ? "bad" : "default"}
                  testId="ops-kpi-failed" />
        <KpiCard label="Overgeslagen" value={kpis.skipped_total}
                  icon={AlertTriangle} tone={kpis.skipped_total ? "warn" : "default"}
                  testId="ops-kpi-skipped" />
        <KpiCard label="Totaal gepland" value={kpis.scheduled_total}
                  icon={Clock} testId="ops-kpi-scheduled-total" />
        <KpiCard label="Nu processing" value={kpis.processing}
                  icon={Play} testId="ops-kpi-processing" />
        <KpiCard label="Zendingen vandaag" value={kpis.shipments_affected_today}
                  icon={Package} testId="ops-kpi-shipments" />
        <KpiCard label="Consolidaties vandaag" value={kpis.consolidations_affected_today}
                  icon={Boxes} testId="ops-kpi-consolidations" />
      </div>

      <Card className="border-gray-200">
        <CardHeader className="border-b border-gray-100">
          <div className="flex items-center gap-1 overflow-x-auto">
            {[
              ["today", `Vandaag (${segments.today?.length || 0})`],
              ["upcoming", `Komende updates (${segments.upcoming?.length || 0})`],
              ["failed", `Mislukt (${segments.failed?.length || 0})`],
              ["skipped", `Overgeslagen (${segments.skipped?.length || 0})`],
              ["recent_executed", `Recent uitgevoerd (${segments.recent_executed?.length || 0})`],
            ].map(([k, label]) => (
              <button key={k}
                onClick={() => setSelectedSegment(k)}
                data-testid={`ops-segment-${k}`}
                className={
                  `px-4 py-2 text-sm border-b-2 whitespace-nowrap transition ` +
                  (selectedSegment === k
                    ? "border-[#082c50] text-[#082c50] font-medium"
                    : "border-transparent text-gray-500 hover:text-gray-800")
                }
              >
                {label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {segments[selectedSegment]?.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entiteit</TableHead>
                  <TableHead>Nieuwe status</TableHead>
                  <TableHead>Gepland op</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Details</TableHead>
                  {(selectedSegment === "failed" || selectedSegment === "skipped") && (
                    <TableHead className="text-right">Actie</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {segments[selectedSegment].map((ev) => (
                  <React.Fragment key={ev.id}>
                    <EventRow ev={ev} />
                    {(selectedSegment === "failed" || selectedSegment === "skipped") && (
                      <TableRow key={`${ev.id}-actions`}>
                        <TableCell colSpan={6} className="text-right py-0 pb-2 border-t-0">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleRetry(ev.id)}
                            disabled={!!retryLoading[ev.id]}
                            data-testid={`ops-retry-${ev.id}`}
                          >
                            <RefreshCw className={`w-3 h-3 mr-1 ${retryLoading[ev.id] ? "animate-spin" : ""}`} />
                            Opnieuw inplannen
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-sm text-gray-400"
                  data-testid={`ops-segment-empty-${selectedSegment}`}>
              Geen events in dit segment.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ConsolidationPlanner({ onOpenConsolidation }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchPlanner = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/consolidations-planner`,
        { headers: authHeaders() },
      );
      setData(r.data);
    } catch (e) {
      toast.error(
        `Planner laden mislukt: ${e.response?.data?.detail || e.message}`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlanner(); }, [fetchPlanner]);

  return (
    <div className="space-y-4" data-testid="ops-consolidation-planner">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-black text-[#082c50] font-chivo">
            Consolidatie-planner
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {data.total || 0} actieve consolidaties op basis van ETD.
          </p>
        </div>
        <Button variant="outline" onClick={fetchPlanner} disabled={loading}
                 data-testid="ops-planner-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Verversen
        </Button>
      </div>

      {data.items.length === 0 ? (
        <div className="border border-gray-200 rounded-md p-10 text-center text-gray-400"
              data-testid="ops-planner-empty">
          Geen actieve consolidaties gevonden.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.items.map((c) => {
            const mode = c.measurement?.startsWith("aero") ? "air"
                       : c.measurement?.startsWith("maritimo") ? "sea"
                       : c.type === "courier" ? "courier"
                       : "ground";
            const Icon = TRANSPORT_ICONS[mode] || Boxes;
            return (
              <button key={c.id}
                onClick={() => onOpenConsolidation?.(c.id)}
                data-testid={`ops-planner-card-${c.id}`}
                className="text-left border border-gray-200 rounded-md p-4 hover:border-[#082c50] hover:shadow-sm transition"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon className="w-4 h-4 text-[#082c50]" />
                      <span className="font-mono font-semibold text-[#082c50]">
                        {c.consolidation_number}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {c.departure_hub_label} → {c.destination_hub_label}
                    </div>
                  </div>
                  <Badge variant="outline" className={STATE_COLORS[c.status] || "bg-gray-50"}>
                    {c.status}
                  </Badge>
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-xs">
                  <dt className="text-gray-500">ETD</dt>
                  <dd className="font-medium">{c.etd ? fmtDate(c.etd) : "—"}</dd>
                  <dt className="text-gray-500">ETA</dt>
                  <dd className="font-medium">{c.eta ? fmtDate(c.eta) : "—"}</dd>
                  <dt className="text-gray-500">Zendingen</dt>
                  <dd className="font-medium">{c.shipment_count}</dd>
                  <dt className="text-gray-500">Gewicht</dt>
                  <dd className="font-medium">
                    {c.totals?.weight_kg ? `${c.totals.weight_kg.toFixed(1)} kg` : "—"}
                  </dd>
                </dl>
                {c.next_scheduled_event && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                    <span className="text-gray-500">Volgende update</span>
                    <span className="font-medium">
                      {c.next_scheduled_event.new_status} · {fmtDate(c.next_scheduled_event.scheduled_for)}
                    </span>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-end text-xs text-[#082c50] font-medium">
                  Openen <ChevronRight className="w-3 h-3" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusFlowTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/status-flow-templates`,
          { headers: authHeaders() },
        );
        if (mounted) setTemplates(r.data.items || []);
      } catch (e) {
        if (mounted) toast.error(`Templates laden mislukt: ${e.message}`);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="space-y-4" data-testid="ops-flow-templates">
      <div>
        <h3 className="text-lg font-black text-[#082c50] font-chivo">
          Statusflow-templates
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Vooraf ingestelde workflows per transport-modus. Toe te passen op elke
          shipment of consolidatie.
        </p>
      </div>
      {loading ? (
        <div className="text-center text-gray-400 p-6">Laden...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t) => {
            const Icon = TRANSPORT_ICONS[t.transport_mode] || Boxes;
            return (
              <Card key={t.code} data-testid={`ops-template-${t.code}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Icon className="w-4 h-4 text-[#082c50]" />
                      {t.name}
                    </CardTitle>
                    <Badge variant="outline">{t.transport_mode}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t.description}</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <ol className="space-y-1.5 text-sm">
                    {t.steps?.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-xs text-gray-400 font-mono mt-0.5 min-w-[1.5rem]">
                          {i + 1}.
                        </span>
                        <div className="flex-1">
                          <div className="text-gray-800">{s.label_nl || s.status}</div>
                          <div className="text-xs text-gray-500">
                            {s.rule_type === "absolute" && "Handmatige datum"}
                            {s.rule_type === "relative_to_etd" && `ETD ${s.offset_days >= 0 ? "+" : ""}${s.offset_days}d`}
                            {s.rule_type === "relative_to_eta" && `ETA ${s.offset_days >= 0 ? "+" : ""}${s.offset_days}d`}
                            {s.notify_customer && " · notificeert klant"}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function TrackingAutomationSection() {
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="space-y-6" data-testid="consolidation-automation-section">
      <div className="flex items-baseline gap-2">
        <h1 className="font-chivo font-black text-2xl text-[#082c50]" data-testid="consolidation-automation-title">
          Consolidation Automation
        </h1>
        <span className="text-xs text-slate-500 uppercase tracking-wider font-bold">
          Operationeel planningscentrum
        </span>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="ops-tabs">
          <TabsTrigger value="dashboard" data-testid="ops-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="planner" data-testid="ops-tab-planner">Consolidatie-planner</TabsTrigger>
          <TabsTrigger value="templates" data-testid="ops-tab-templates">Statusflow-templates</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6">
          <OpsDashboard />
        </TabsContent>
        <TabsContent value="planner" className="mt-6">
          <ConsolidationPlanner />
        </TabsContent>
        <TabsContent value="templates" className="mt-6">
          <StatusFlowTemplates />
        </TabsContent>
      </Tabs>
    </div>
  );
}
